import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTheme } from 'styled-components';
import { useTranslation } from 'react-i18next';
import {
  StyledToolOptionsWrapper,
  StyledDropdownButtonWrapper,
  ToolbarDropdownButton,
  DropdownButtonMenu,
  DropdownButtonMenuOption,
} from './styled/StyledDropdownButton';

export const DropdownButton = ({ selectHandler, tool, enabled, label, icon, options, isPersistent, isTool, onDropdownMenuToggle, className, menuClassName, disabled = false }) => {
  const { t } = useTranslation();
  const iconSize = '1.4em';
  const subIconSize = '1em';
  const [isOpen, setIsOpen] = useState(false);
  const [buttonLabel, setButtonLabel] = useState(label);
  const [dropdownMenuBackgroundColor, setDropdownMenuBackgroundColor] = useState("rgba(0, 0, 0, 0.8)");
  const [showInputBox, setShowInputBox] = useState(false);
  const dropdownRef = useRef();
  const menuRef = useRef();
  const feedbackTimeoutRef = useRef();
  const [menuPosition, setMenuPosition] = useState(null);
  const theme = useTheme();
  const desktopBreakpoint = Number.parseInt(theme?.sizes?.desktop || '1080', 10);
  const isCompactViewport = typeof window !== 'undefined' && window.innerWidth <= desktopBreakpoint;
  const makeIcon = (icon, size) => {
    if (!icon) return null;
    const Icon = icon;
    return <Icon size={size} />;
  };

  const handlerOptionClick = (option) => {
    if (disabled) return;
    const isDisabled = typeof option.disabled === 'function' ? option.disabled() : option.disabled;
    if (isDisabled) return;

    if (option.callback !== undefined) {
      option.callback();
    } else if (option.renderInputBox !== undefined) {
      toggleShowInputBox();
    } else {
      selectHandler(option);
    }

    if (option.feedbackLabel) {
      setButtonLabel(option.feedbackLabel);
      clearTimeout(feedbackTimeoutRef.current);
      feedbackTimeoutRef.current = setTimeout(() => setButtonLabel(label), 2000);
    }
  };

  const toggleShowInputBox = () => {
    setShowInputBox((prev) => !prev);
  };

  const handleClickOutside = (event) => {
    if (
      dropdownRef.current
      && !dropdownRef.current.contains(event.target)
      && !menuRef.current?.contains(event.target)
    ) {
      setIsOpen(false);
      setShowInputBox(false);
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      clearTimeout(feedbackTimeoutRef.current);
    };
  }, []);

  useEffect(() => {
    if (!disabled) return;
    setIsOpen(false);
    setShowInputBox(false);
  }, [disabled]);

  useEffect(() => {
    const handleDropdownToggle = () => {
      if (onDropdownMenuToggle) {
        onDropdownMenuToggle(!isOpen);
        setDropdownMenuBackgroundColor("transparent");
      }
    };
    handleDropdownToggle();

  }, [isOpen]);

  useLayoutEffect(() => {
    if (!isOpen) return undefined;

    const updateMenuPosition = () => {
      const anchor = dropdownRef.current?.getBoundingClientRect();
      const menu = menuRef.current?.getBoundingClientRect();
      if (!anchor || !menu) return;

      const edgePadding = 8;
      const left = Math.max(edgePadding, Math.min(anchor.left, window.innerWidth - menu.width - edgePadding));
      const top = anchor.bottom + menu.height > window.innerHeight
        ? Math.max(edgePadding, anchor.top - menu.height)
        : anchor.bottom;
      setMenuPosition({ top, left });
    };

    updateMenuPosition();
    window.addEventListener('resize', updateMenuPosition);
    window.addEventListener('scroll', updateMenuPosition, true);
    return () => {
      window.removeEventListener('resize', updateMenuPosition);
      window.removeEventListener('scroll', updateMenuPosition, true);
    };
  }, [isOpen, isCompactViewport]);

  return (
    <StyledToolOptionsWrapper ref={dropdownRef} >
      <StyledDropdownButtonWrapper onClick={() => {
        if (disabled) return;
        (!isOpen ? setIsOpen(true) : setIsOpen(false));
        isTool && selectHandler(tool);
      }}>
        <ToolbarDropdownButton
          disabled={disabled}
          className={`${enabled || ''} ${className || ''}`}
        >
          {makeIcon(icon, (tool && tool.iconSize) ?? iconSize)}
          <span>{t(buttonLabel)} <span className='toolArrow'>&#9660;</span></span>
        </ToolbarDropdownButton>
      </StyledDropdownButtonWrapper>
      {isOpen && typeof document !== 'undefined' && createPortal(
        <DropdownButtonMenu
          ref={menuRef}
          className={menuClassName}
          data-toolbar-dropdown-menu="true"
          onClick={!isPersistent ? (() => setIsOpen(false)) : (() => setIsOpen(true))}
          dropdownMenuBackgroundColor={dropdownMenuBackgroundColor}
          style={{
            position: 'fixed',
            top: menuPosition?.top ?? 0,
            left: menuPosition?.left ?? 0,
            visibility: menuPosition ? 'visible' : 'hidden',
          }}
        >
          {options.map((option) => {
            const optionLabel = typeof option.label === 'function' ? option.label() : option.label;
            return (
              <DropdownButtonMenuOption
                onClick={() => handlerOptionClick(option)}
                disabled={typeof option.disabled === 'function' ? option.disabled() : option.disabled}
                key={optionLabel}
              >
                { option.icon && makeIcon(option.icon, subIconSize) }
                { optionLabel && (<span>{t(optionLabel)}</span>) }
              </DropdownButtonMenuOption>
            );
          })}
        </DropdownButtonMenu>,
        document.body
      )}
      {options.map((option, i) => {
        if (option.renderInputBox) {
          const {InputBox} = option.renderInputBox;
          return (
            <div key={option.label + '-InputBox-' + i} className={showInputBox ? '' : 'hide'}>
              <InputBox toggleShowInputBox={toggleShowInputBox} />
            </div>
          )
        }
      })}
    </StyledToolOptionsWrapper>
  );
};
